export class NotFoundError extends Error {
  constructor(msg: string) {
    super(msg);
    Object.defineProperty(this, 'name', {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'NotFoundError'
    });
  }
}

export class BadRequestError extends Error {
  readonly code?: string;
  readonly data?: { field?: string; reason?: string; messageId?: string };
  readonly status?: number;
  constructor(
    msg: string,
    response?: {
      code?: string;
      data?: { field?: string; reason?: string; messageId?: string };
    },
    status?: number
  ) {
    super(msg);
    Object.defineProperty(this, 'name', {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'BadRequestError'
    });
    this.code = response?.code;
    this.data = response?.data;
    this.status = status;
  }
}

export function isOptimisticLockError(
  error: unknown
): error is BadRequestError {
  if (!(error instanceof BadRequestError)) return false;
  return (
    error.status === 409 ||
    error.code === '40009' ||
    error.code === 'COMMON_CONCURRENT_UPDATE' ||
    error.code === 'WORKFLOW_STATUS_INVALID' ||
    error.data?.messageId === 'COMMON_CONCURRENT_UPDATE' ||
    error.data?.messageId === 'WORKFLOW_STATUS_INVALID'
  );
}

export class ResponseError extends Error {
  constructor(response: Response, msg: string) {
    super(msg);
    Object.defineProperty(this, 'response', {
      enumerable: true,
      configurable: true,
      writable: true,
      value: response
    });
    Object.defineProperty(this, 'name', {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'ResponseError'
    });
  }
}

export class UnauthorizedError extends Error {
  constructor(msg: string) {
    super(msg);
    Object.defineProperty(this, 'name', {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'UnauthorizedError'
    });
  }
}

export class ForbiddenError extends Error {
  constructor(msg: string) {
    super(msg);
    Object.defineProperty(this, 'name', {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'ForbiddenError'
    });
  }
}

export class FetchError extends Error {
  constructor(cause: any, msg: string) {
    super(msg);
    Object.defineProperty(this, 'cause', {
      enumerable: true,
      configurable: true,
      writable: true,
      value: cause
    });
    Object.defineProperty(this, 'name', {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'FetchError'
    });
  }
}

export class RequiredError extends Error {
  constructor(field: any, msg: string) {
    super(msg);
    Object.defineProperty(this, 'field', {
      enumerable: true,
      configurable: true,
      writable: true,
      value: field
    });
    Object.defineProperty(this, 'name', {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'RequiredError'
    });
  }
}

// Only payment-method field errors are handled inline by booking forms.
export function getPaymentMethodError(error: unknown): string | undefined {
  if (
    !(error instanceof BadRequestError) ||
    error.data?.field !== 'paymentMethodCombinedCode'
  )
    return undefined;
  const messages: Record<string, string> = {
    first_payment_method_locked_after_payment:
      '初回支払済みのため、初回支払方法は変更できません。',
    diff_payment_method_locked_after_service_date:
      '乗車日の翌日以降は、差額支払方法を変更できません。',
    payment_method_locked_during_processing:
      '支払処理中のため、支払方法は変更できません。',
    payment_method_business_date_missing:
      '乗車日が取得できないため、支払方法を変更できません。予約情報をご確認ください。'
  };
  return (
    messages[error.data.reason ?? ''] ||
    error.message ||
    '支払方法をご確認ください。'
  );
}
